import { useEffect } from "react";
import { publicI18n, localeMeta } from "@/i18n";

const SITE_URL = "https://shoppingdobem.com.br";

interface SeoConfig {
  title: string;
  description: string;
  canonical?: string;
  noindex?: boolean;
  ogImage?: string;
  ogType?: string;
  enabled?: boolean;
}

// Escreve apenas quando o valor muda: assim os metadados já injetados na borda
// (páginas de loja) são preservados, sem sobrescrita nem "piscada".
function ensureMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  if (el.getAttribute("content") !== content) el.setAttribute("content", content);
  return el;
}

function ensureLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  if (el.getAttribute("href") !== href) el.setAttribute("href", href);
  return el;
}

export function usePageSeo({ title, description, canonical, noindex, ogImage, ogType, enabled = true }: SeoConfig) {
  useEffect(() => {
    if (!enabled) return;
    const originalTitle = document.title;
    if (document.title !== title) document.title = title;

    ensureMeta("name", "description", description);
    ensureMeta("property", "og:title", title);
    ensureMeta("property", "og:description", description);
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", description);

    // Public locale → og:locale + html lang. publicI18n stays in sync with the
    // global site language (countries.is_default), so this updates reactively
    // when an admin switches the public language.
    const meta = localeMeta(publicI18n.language);
    ensureMeta("property", "og:locale", meta.ogLocale);
    document.documentElement.lang = meta.htmlLang;

    if (ogType) {
      ensureMeta("property", "og:type", ogType);
    }

    if (ogImage) {
      ensureMeta("property", "og:image", ogImage);
      ensureMeta("name", "twitter:image", ogImage);
    }

    const canonicalUrl = canonical || `${SITE_URL}${window.location.pathname}`;
    ensureLink("canonical", canonicalUrl);
    ensureMeta("property", "og:url", canonicalUrl);

    // Robots
    let robotsEl = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (noindex) {
      if (!robotsEl) {
        robotsEl = document.createElement("meta");
        robotsEl.setAttribute("name", "robots");
        document.head.appendChild(robotsEl);
      }
      robotsEl.setAttribute("content", "noindex, nofollow");
    } else if (robotsEl) {
      robotsEl.setAttribute("content", "index, follow");
    }

    return () => {
      document.title = originalTitle;
      // Reset robots to index,follow when leaving a noindex page so the
      // tag does not contaminate subsequent client-side navigations.
      if (noindex) {
        const el = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
        if (el) el.setAttribute("content", "index, follow");
      }
    };
  }, [title, description, canonical, noindex, ogImage, ogType, enabled]);
}
